import {useEffect, useRef} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import {getEmptyImage} from 'react-dnd-html5-backend';
import {DragDropType, ExplorerContentTypes} from '../../constants/dataEnum';

function isProtocolDroppable(protocol) {
  return protocol && protocol.type === ExplorerContentTypes.FOLDER;
}

function useDragDropProtocol({protocol, onDropped, onBeginDrag, onEndDrag, isRunning, disableDrag}) {
  const prevIsDragging = useRef(false);

  const [{isDragging}, drag, preview] = useDrag({
    item: {
      id: protocol.ID,
      type: DragDropType.PROTOCOL_EXPLORER_ITEM,
      protocol,
    },
    collect: (monitor) => {
      const currentIsDragging = monitor.isDragging();

      if (prevIsDragging.current !== currentIsDragging) {
        prevIsDragging.current = currentIsDragging;

        if (currentIsDragging && onBeginDrag && typeof onBeginDrag === 'function') {
          onBeginDrag(protocol);
        }
      }

      return {isDragging: currentIsDragging};
    },
    canDrag: !!protocol && !isRunning && !disableDrag,
    end: (item) => {
      if (onEndDrag && typeof onEndDrag === 'function') {
        onEndDrag(item?.protocol);
      }
    },
  });

  const [{isDragOver}, drop] = useDrop({
    accept: DragDropType.PROTOCOL_EXPLORER_ITEM,
    drop: (item) => {
      if (onDropped && typeof onDropped === 'function') {
        const dragProtocolId = item.protocol.ID;
        const dropProtocolId = protocol.ID;

        if (dragProtocolId === dropProtocolId) return;

        onDropped(dragProtocolId, dropProtocolId);
      }
    },
    hover: () => undefined,
    collect: (monitor) => ({
      isDragOver: monitor.isOver() && isProtocolDroppable(protocol),
    }),
    canDrop: () => isProtocolDroppable(protocol),
  });

  useEffect(() => {
    // remove default drag preview image
    preview(getEmptyImage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {drag, drop, isDragging, isDragOver};
}

export default useDragDropProtocol;
